import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import App from './App';
import './index.css';

// Create a client for React Query.
// Defaults tuned for instant tab-revisits within a practice: cached data paints
// immediately and is refreshed in the background when older than staleTime.
//  - staleTime 3 min: revisiting a tab within this window serves cache without a
//    network round-trip (the page paints instantly). After 3 min it refetches in
//    the background while still showing the cached data.
//  - cacheTime 10 min: how long an unused query stays in memory after the last
//    consumer unmounts, so navigating away and back is still warm.
//  - refetchOnWindowFocus false: tab/alt-tab focus does not trigger refetches —
//    mild staleness is acceptable and avoids spurious reloads.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 180000,           // 3 min
      cacheTime: 600000,           // 10 min
      refetchOnWindowFocus: false,
    },
  },
});

// Registers the no-op worker from public/sw.js. Its only job is to make the app
// installable, which is what gives it a taskbar icon for navigator.setAppBadge().
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

// Chrome fires this once, shortly after load — usually before React has mounted
// the component that renders the install banner, and the event is gone if nobody
// caught it. Stash it here so the banner can pick it up whenever it mounts.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.deskbotInstallPrompt = e;
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
); 